import React, { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
} from 'date-fns';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ResourceBookingSchema } from '../../data/types';
import { findBookingsForDay, formatBookingTimeRange } from '../../utils/helpers';
import { capitalizeWords, getBookingStatusColor } from '../../utils/helpers';
import moment from 'moment-timezone';

type BookingsCalendarViewProps = {
  bookings: ResourceBookingSchema[];
  onViewDetails?: (booking: ResourceBookingSchema) => void;
};

const BookingsCalendarView: React.FC<BookingsCalendarViewProps> = ({
  bookings,
  onViewDetails,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const daysInMonth = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const bookingsForSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    return findBookingsForDay(moment(selectedDay), bookings);
  }, [selectedDay, bookings]);

  const getDayBookings = (day: Date) =>
    findBookingsForDay(moment(day), bookings);

  return (
    <div className="w-full px-6">
      <div className="flex flex-row items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
          className="flex items-center justify-center w-8 h-8 rounded-full border-[1px] border-[#E9E9E9] hover:bg-[#f5f5f5] text-[#013847]"
        >
          <ArrowBackIosIcon className="text-[#013847] m-auto" fontSize="small"  />
        </button>
        <h2 className="font-raleway font-semibold text-lg text-[#013847]">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
          className="flex items-center justify-center w-8 h-8 rounded-full border-[1px] border-[#E9E9E9] hover:bg-[#f5f5f5] text-[#013847]"
        >
          <ArrowForwardIosIcon className="text-[#013847] m-auto" fontSize="small" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-raleway font-semibold text-[#8A8A8A] py-2"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {daysInMonth.map((day) => {
          const dayBookings = getDayBookings(day);
          const isSelected = selectedDay && isSameDay(day, selectedDay);
          const inMonth = isSameMonth(day, currentMonth);
          const isToday = isSameDay(day, new Date());

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={`min-h-[72px] p-1 border rounded-lg text-left transition-colors ${
                isSelected
                  ? 'border-[#013847] bg-[#e5ebec]'
                  : 'border-[#E9E9E9] hover:bg-[#FAFAFA]'
              } ${!inMonth ? 'opacity-40' : ''}`}
            >
              <span
                className={`inline-flex items-center justify-center w-6 h-6 text-xs font-raleway rounded-full ${
                  isToday ? 'bg-[#013847] text-white font-bold' : 'text-[#013847]'
                }`}
              >
                {format(day, 'd')}
              </span>
              <div className="flex flex-col gap-0.5 mt-1">
                {dayBookings.slice(0, 2).map((b) => {
                  const color = getBookingStatusColor(b.status || 'pending');
                  return (
                    <div
                      key={b.id}
                      className="text-[9px] font-raleway truncate px-1 py-0.5 rounded"
                      style={{
                        backgroundColor: color.bgColor,
                        color: color.primaryTextColor,
                      }}
                    >
                      {b.resource_name}
                    </div>
                  );
                })}
                {dayBookings.length > 2 && (
                  <span className="text-[9px] text-[#8A8A8A] pl-1">
                    +{dayBookings.length - 2} more
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-6 border border-[#E9E9E9] rounded-xl p-4 bg-[#FAFAFA]">
          <h3 className="font-raleway font-semibold text-[#013847] mb-3">
            {format(selectedDay, 'EEEE, MMMM d, yyyy')}
            <span className="text-[#8A8A8A] font-normal ml-2">
              ({bookingsForSelectedDay.length} booking
              {bookingsForSelectedDay.length !== 1 ? 's' : ''})
            </span>
          </h3>

          {bookingsForSelectedDay.length === 0 ? (
            <p className="text-sm font-raleway text-[#8A8A8A]">No bookings on this day.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {bookingsForSelectedDay.map((booking) => {
                const color = getBookingStatusColor(booking.status || 'pending');
                return (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => onViewDetails?.(booking)}
                    className="flex flex-row items-center justify-between p-3 bg-white border border-[#E9E9E9] rounded-lg hover:shadow-sm text-left w-full"
                  >
                    <div>
                      <p className="font-raleway font-semibold text-[#013847]">
                        {booking.resource_name}
                      </p>
                      <p className="text-xs font-raleway text-[#8A8A8A] mt-0.5">
                        {formatBookingTimeRange(booking)}
                        {booking.resource_type && ` · ${booking.resource_type}`}
                      </p>
                    </div>
                    {booking.status && (
                      <span
                        className="px-2 py-1 rounded text-xs font-raleway font-bold capitalize flex-shrink-0"
                        style={{
                          backgroundColor: color.bgColor,
                          color: color.primaryTextColor,
                        }}
                      >
                        {capitalizeWords(booking.status.replace(/_/g, ' '))}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingsCalendarView;
